// src/components/BCP/RiskAssessmentStep.tsx - Enhanced with AI suggestions and risk visualization
import React from 'react';
import { RiskAssessmentStepProps } from '../../types/bcpForm';
import { BCPSuggestions } from '../../services/api';
import { UserPreferences } from '../../types/userPreferences';
import { isStep1Complete } from '../../utils/aiAnalysisUtils';
import InfoTooltip from './InfoTooltip';

interface EnhancedRiskAssessmentStepProps extends RiskAssessmentStepProps {
  suggestions?: BCPSuggestions | null;
  isLoadingSuggestions?: boolean;
  onGetSuggestions?: () => void;
  businessProfile?: any;
  suggestionsApplied?: boolean;
  userPreferences?: UserPreferences | null;
}

const defaultRisks = [
  'Natural Disasters',
  'Cyber Attacks',
  'Supply Chain Disruption',
  'Key Personnel Loss',
  'Equipment Failure'
];

// Helper function to filter cyber attacks related risks
const filterCyberAttacks = (risks: string[], includeCyber: boolean): string[] => {
  if (includeCyber) return risks;
  return risks.filter(risk =>
    !risk.toLowerCase().includes('cyber') &&
    !risk.toLowerCase().includes('cybersecurity') &&
    !risk.toLowerCase().includes('data breach') &&
    !risk.toLowerCase().includes('ransomware') &&
    !risk.toLowerCase().includes('phishing')
  );
};

const defaultLocationRisks = [
  'Earthquakes',
  'Floods',
  'Hurricanes',
  'Wildfires',
  'Extreme Weather'
];

// Helper function to filter cyber attacks related location risks
const filterCyberLocationRisks = (risks: string[], includeCyber: boolean): string[] => {
  if (includeCyber) return risks;
  return risks.filter(risk =>
    !risk.toLowerCase().includes('cyber') &&
    !risk.toLowerCase().includes('cybersecurity')
  );
};


const RiskAssessmentStep: React.FC<EnhancedRiskAssessmentStepProps> = ({
  risks,
  setRisks,
  suggestions,
  isLoadingSuggestions,
  onGetSuggestions,
  businessProfile,
  suggestionsApplied,
  userPreferences
}) => {
  // Check if cyber assessment is enabled
  const isCyberAssessmentEnabled = userPreferences?.cyber_assessment_interest !== false;

  // Check if section 1 is complete enough to show risks
  const isSection1Complete = businessProfile ? isStep1Complete(businessProfile) : false;

  return (
  <div className="space-y-6">
    <div>
      <h2 className="mb-2 text-2xl font-bold text-gray-900">
        Risk Assessment
        <InfoTooltip content="A structured process used to identify, analyze, and evaluate potential threats that could disrupt an organization's ability to operate, and to determine the likelihood and impact of those threats." />
      </h2>
      <p className="text-gray-600">Help us identify potential threats to your business</p>
    </div>

    {/* Section 1 Incomplete Message */}
    {!isSection1Complete && (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Complete Business Information First
            </h3>
            <p className="mt-1 text-sm text-yellow-700">
              Please complete all required fields in the Business Information section before proceeding with risk assessment. This ensures we can provide accurate, tailored risk analysis for your specific business.
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Success Message */}
    {suggestionsApplied && (
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-green-800">
              AI suggestions applied successfully!
            </p>
            <p className="text-sm text-green-700">
              Your risk assessment has been updated with AI-recommended risks and functions.
            </p>
          </div>
        </div>
      </div>
    )}

    {/* AI Suggestions Button */}
    {businessProfile && (
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">🤖 AI-Powered Risk Assessment</h3>
            <p className="text-sm text-blue-700">
              Based on your business profile: {businessProfile.business_name} ({businessProfile.industry}) in {businessProfile.city}, {businessProfile.state}
            </p>
          </div>
          <button
            onClick={() => {
              console.log('🖱️ [BUTTON] ===== AI SUGGESTIONS BUTTON CLICKED =====');
              console.log('🖱️ [BUTTON] Button state - isLoadingSuggestions:', isLoadingSuggestions);
              console.log('🖱️ [BUTTON] Button state - suggestions:', suggestions);
              console.log('🖱️ [BUTTON] Button state - businessProfile:', businessProfile);
              console.log('🖱️ [BUTTON] Calling onGetSuggestions handler...');
              if (onGetSuggestions) {
                onGetSuggestions();
              } else {
                console.error('❌ [BUTTON] onGetSuggestions handler is undefined!');
              }
            }}
            disabled={isLoadingSuggestions}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isLoadingSuggestions
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoadingSuggestions ? (
              <>
                <div className="inline mr-2 w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                Getting Suggestions...
              </>
            ) : (
              'Get AI Suggestions'
            )}
          </button>
        </div>

        {suggestions && (
          <div className="pt-3 mt-3 border-t border-blue-200">
            <div className="text-sm text-blue-700">
              <strong>Business Size Considerations:</strong> {suggestions.business_size_considerations.join(', ')}
            </div>
          </div>
        )}
      </div>
    )}


    {/* Risk Selection - Only show when section 1 is complete */}
    {isSection1Complete && (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-700">
            Primary Concerns
            {suggestions && (
              <span className="ml-2 text-xs text-blue-600">(AI-Enhanced)</span>
            )}
          </label>
        <div className="space-y-3">
          {/* Show all risks: defaults + loaded + AI suggestions, with auto-checking for AI matches */}
                    {(() => {
            // Smart deduplication: Check if AI suggestions match existing risks
            const allRisks = [...filterCyberAttacks(defaultRisks, isCyberAssessmentEnabled)];
            const aiSuggestions = suggestions?.suggested_risks || [];

            // Add loaded risks from backend that aren't already in defaults
            const loadedRisks = risks.primary_concerns || [];
            console.log('🔍 [RISK ASSESSMENT] Current risks state:', {
              risks,
              loadedRisks,
              allRisksBefore: [...allRisks],
              isCyberAssessmentEnabled
            });

            loadedRisks.forEach(loadedRisk => {
              if (!allRisks.includes(loadedRisk)) {
                allRisks.push(loadedRisk);
              }
            });

            // Add AI suggestions that aren't already in the list
            aiSuggestions.forEach(aiRisk => {
              if (!allRisks.includes(aiRisk)) {
                allRisks.push(aiRisk);
              }
            });

                                    // Helper function to check if risks are semantically similar
            const isSimilarRisk = (risk1: string, risk2: string): boolean => {
              const normalized1 = risk1.toLowerCase().replace(/[^a-z0-9\s]/g, '');
              const normalized2 = risk2.toLowerCase().replace(/[^a-z0-9\s]/g, '');

              // Check for exact matches
              if (normalized1 === normalized2) return true;

              // Check for very close matches (e.g., "cyber attacks" vs "cyber attack")
              if (normalized1.replace(/\s+/g, '') === normalized2.replace(/\s+/g, '')) return true;

              // Check for singular/plural matches
              if (normalized1.replace(/s$/, '') === normalized2.replace(/s$/, '') ||
                  normalized1 === normalized2.replace(/s$/, '') ||
                  normalized1.replace(/s$/, '') === normalized2) return true;

              // Intelligent semantic matching based on core concepts
              const semanticMatches = [
                // Cyber Security
                {
                  patterns: ['cyber attack', 'cyberattack', 'cybersecurity', 'data breach', 'ransomware', 'phishing'],
                  match: (r1: string, r2: string) => {
                    const cyber1 = r1.includes('cyber') || r1.includes('breach') || r1.includes('ransomware') || r1.includes('phishing');
                    const cyber2 = r2.includes('cyber') || r2.includes('breach') || r2.includes('ransomware') || r2.includes('phishing');
                    return cyber1 && cyber2;
                  }
                },
                // Supply Chain
                {
                  patterns: ['supply chain', 'supply disruption', 'supplier', 'hardware', 'software', 'access'],
                  match: (r1: string, r2: string) => {
                    const supply1 = r1.includes('supply') || r1.includes('chain') || r1.includes('supplier');
                    const supply2 = r2.includes('supply') || r2.includes('chain') || r2.includes('supplier');
                    return supply1 && supply2;
                  }
                },
                // Personnel/Staff
                {
                  patterns: ['personnel', 'staff', 'employee', 'key', 'loss', 'unavailability', 'illness', 'resignation'],
                  match: (r1: string, r2: string) => {
                    const personnel1 = r1.includes('personnel') || r1.includes('staff') || r1.includes('employee') || r1.includes('key');
                    const personnel2 = r2.includes('personnel') || r2.includes('staff') || r2.includes('employee') || r2.includes('key');
                    return personnel1 && personnel2;
                  }
                },
                // Equipment/Infrastructure
                {
                  patterns: ['equipment', 'hardware', 'infrastructure', 'server', 'network', 'system', 'failure'],
                  match: (r1: string, r2: string) => {
                    const equip1 = r1.includes('equipment') || r1.includes('hardware') || r1.includes('infrastructure') || r1.includes('server') || r1.includes('system');
                    const equip2 = r2.includes('equipment') || r2.includes('hardware') || r2.includes('infrastructure') || r2.includes('server') || r2.includes('system');
                    return equip1 && equip2;
                  }
                },
                // Data/Information
                {
                  patterns: ['data', 'information', 'customer data', 'system failure', 'security breach'],
                  match: (r1: string, r2: string) => {
                    const data1 = r1.includes('data') || r1.includes('information') || r1.includes('customer');
                    const data2 = r2.includes('data') || r2.includes('information') || r2.includes('customer');
                    return data1 && data2;
                  }
                }
              ];

              // Check semantic matches
              for (const semanticMatch of semanticMatches) {
                if (semanticMatch.match(normalized1, normalized2)) {
                  return true;
                }
              }

              return false;
            };

            // Process AI suggestions: only add truly new risks
            aiSuggestions.forEach(aiRisk => {
              const isDuplicate = allRisks.some(existingRisk => isSimilarRisk(aiRisk, existingRisk));
              if (!isDuplicate) {
                allRisks.push(aiRisk);
              }
            });

            // Clean up the risks array to remove duplicates and normalize names
            const cleanedRisks = allRisks.filter((risk, index, self) => {
              // Remove empty or invalid risks
              if (!risk || risk.trim() === '') return false;

              // Remove impact levels that got mixed in
              const impactLevels = ['low', 'medium', 'high', 'critical', 'minor', 'moderate', 'major', 'catastrophic'];
              if (impactLevels.includes(risk.toLowerCase())) return false;

              // Check for duplicates using semantic matching
              const isDuplicate = self.slice(0, index).some(existingRisk => isSimilarRisk(risk, existingRisk));
              return !isDuplicate;
            });

            // Normalize risk names to readable format
            const normalizedRisks = cleanedRisks.map(risk => {
              // Convert underscored names to readable format
              if (risk.includes('_')) {
                return risk
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase());
              }
              return risk;
            });

            // Apply cyber filtering to the final normalized risks
            const filteredRisks = filterCyberAttacks(normalizedRisks, isCyberAssessmentEnabled);

            return filteredRisks.map((risk) => {
              const isAISuggested = aiSuggestions.includes(risk) || aiSuggestions.some(aiRisk => isSimilarRisk(aiRisk, risk));
              const isChecked = risks.primary_concerns.includes(risk) || risks.primary_concerns.some(existingRisk => isSimilarRisk(existingRisk, risk));

              return (
                <label key={risk} className="flex items-center p-3 rounded-lg border border-gray-200 transition-colors cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Add the normalized risk and remove any similar risks
                        setRisks((r) => ({
                          ...r,
                          primary_concerns: [
                            ...r.primary_concerns.filter(existingRisk => !isSimilarRisk(existingRisk, risk)),
                            risk
                          ],
                        }));
                      } else {
                        // Remove the risk and any similar risks
                        setRisks((r) => ({
                          ...r,
                          primary_concerns: r.primary_concerns.filter(existingRisk => !isSimilarRisk(existingRisk, risk)),
                        }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <div className="flex-1 ml-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700">{risk}</span>
                      {isAISuggested && (
                        <span className="px-2 py-1 ml-2 text-xs text-blue-700 bg-blue-100 rounded-full">AI</span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {isAISuggested ? 'AI-suggested risk' : getRiskDescription(risk)}
                    </div>
                  </div>
                </label>
              );
            });
          })()}
        </div>
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Business Location Risks
        </label>
        <div className="space-y-3">
          {(() => {
            // Clean up location risks to remove impact levels and duplicates
            const cleanedLocationRisks = risks.location_risks.filter(risk => {
              // Remove impact levels that got mixed in
              const impactLevels = ['low', 'medium', 'high', 'critical', 'minor', 'moderate', 'major', 'catastrophic'];
              return !impactLevels.includes(risk.toLowerCase());
            });

            // Combine default risks with cleaned existing risks
            const allLocationRisks = [...filterCyberLocationRisks(defaultLocationRisks, isCyberAssessmentEnabled)];
            cleanedLocationRisks.forEach(risk => {
              if (!allLocationRisks.includes(risk)) {
                allLocationRisks.push(risk);
              }
            });

            // Apply cyber filtering to the final location risks
            const filteredLocationRisks = filterCyberLocationRisks(allLocationRisks, isCyberAssessmentEnabled);

            return filteredLocationRisks.map((risk) => {
              const isChecked = risks.location_risks.includes(risk);

              return (
                <label key={risk} className="flex items-center p-3 rounded-lg border border-gray-200 transition-colors cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) =>
                      setRisks((r) => ({
                        ...r,
                        location_risks: e.target.checked
                          ? [...r.location_risks, risk]
                          : r.location_risks.filter((x) => x !== risk),
                      }))
                    }
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <div className="flex-1 ml-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700">{risk}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {getLocationRiskDescription(risk)}
                    </div>
                  </div>
                </label>
              );
            });
          })()}
        </div>
      </div>
    </div>
    )}

  </div>
  );
};

// Helper functions for risk descriptions
function getRiskDescription(risk: string): string {
  const descriptions: Record<string, string> = {
    'Natural Disasters': 'Earthquakes, floods, storms, and other natural events',
    'Cyber Attacks': 'Data breaches, ransomware, and system compromises',
    'Supply Chain Disruption': 'Supplier failures or logistics interruptions',
    'Key Personnel Loss': 'Departure of critical staff members',
    'Equipment Failure': 'Critical system or hardware malfunctions'
  };
  return descriptions[risk] || '';
}

function getLocationRiskDescription(risk: string): string {
  const descriptions: Record<string, string> = {
    'Earthquakes': 'Seismic activity and ground shaking',
    'Floods': 'Water damage from heavy rainfall or overflow',
    'Hurricanes': 'High winds and storm surge damage',
    'Wildfires': 'Fire damage and smoke evacuation',
    'Extreme Weather': 'Severe storms, ice, or temperature events'
  };
  return descriptions[risk] || '';
}


export default RiskAssessmentStep;